import { type EnumType } from '@ncoderz/superenum';

/**
 * Defines the type of a node in the AST.
 * All valid node types should be defined here.
 *
 * Nodes not defined here will not be walked by the AST walker.
 *
 * @enum
 */
const NodeType = {
  unknown: 'unknown', // unknown

  __isDefaultExample: '__isDefaultExample',
  __typeAlias: '__typeAlias',

  action: 'action',
  actionValue: 'actionValue',
  activityType: 'activityType',
  activityTypeValue: 'activityTypeValue',
  additionalSolutions: 'additionalSolutions',
  additionalSolutionsValue: 'additionalSolutionsValue',
  advertisingClickUrl: 'advertisingClickUrl',
  advertisingClickUrlValue: 'advertisingClickUrlValue',
  ageRange: 'ageRange',
  ageRangeValue: 'ageRangeValue',
  aiGenerated: 'aiGenerated',
  aiGeneratedValue: 'aiGeneratedValue',
  alias: 'alias', // bit type (alias)
  alignment: 'alignment',
  allowedBit: 'allowedBit',
  allowedBitValue: 'allowedBitValue',
  allowPrint: 'allowPrint',
  allowPrintValue: 'allowPrintValue',
  alt: 'alt',
  alternativeAnswers: 'alternativeAnswers',
  alternativeAnswersValue: 'alternativeAnswersValue',
  alternativeDefinitions: 'alternativeDefinitions',
  alternativeDefinitionsValue: 'alternativeDefinitionsValue',
  altLangTag: 'altLangTag',
  altLangTagValue: 'altLangTagValue',
  analyticsTag: 'analyticsTag',
  analyticsTagValue: 'analyticsTagValue',
  anchor: 'anchor',
  answer: 'answer',
  appLink: 'appLink',
  attrs: 'attrs',
  audio: 'audio',
  audioEmbed: 'audioEmbed',
  audioLink: 'audioLink',
  author: 'author',
  authorFullName: 'authorFullName',
  authorFullNameValue: 'authorFullNameValue',
  authorJobTitle: 'authorJobTitle',
  authorJobTitleValue: 'authorJobTitleValue',
  authorPseudonym: 'authorPseudonym',
  authorPseudonymValue: 'authorPseudonymValue',
  authorTitle: 'authorTitle',
  authorTitleValue: 'authorTitleValue',
  authorValue: 'authorValue',
  availableClassifications: 'availableClassifications',
  availableClassificationsValue: 'availableClassificationsValue',
  avatarImage: 'avatarImage',
  backgroundWallpaper: 'backgroundWallpaper',
  backgroundWallpaperValue: 'backgroundWallpaperValue',
  backgroundImage: 'backgroundImage',
  backgroundImageValue: 'backgroundImageValue',
  bitLevel: 'bitLevel', // bit level
  bitmarkAst: 'bitmarkAst', // bitmarkAst
  bitmarkVersion: 'bitmarkVersion',
  bits: 'bits', // bits
  bitsValue: 'bitsValue', // bit
  bitType: 'bitType', // bit type
  bitFingerprint: 'bitFingerprint',
  bitFingerprintValue: 'bitFingerprintValue',
  blockId: 'blockId',
  blockIdValue: 'blockIdValue',
  body: 'body',
  bodyBit: 'bodyBit',
  bodyBits: 'bodyBits',
  bodyJson: 'bodyJson',
  bodyParts: 'bodyParts',
  bodyPartsValue: 'bodyPartsValue',
  bodyPartText: 'bodyPartText',
  bodyText: 'bodyText',
  bodyValue: 'bodyValue',
  book: 'book',
  bookAlias: 'bookAlias',
  bookAliasValue: 'bookAliasValue',
  bookAuthor: 'bookAuthor',
  bookBindingType: 'bookBindingType',
  bookDiff: 'bookDiff',
  bookDiffValue: 'bookDiffValue',
  bookIsbn: 'bookIsbn',
  bookLanguage: 'bookLanguage',
  bookNumberOfPages: 'bookNumberOfPages',
  bookOriginalTitle: 'bookOriginalTitle',
  bookPublisher: 'bookPublisher',
  bookPublishingDate: 'bookPublishingDate',
  bookRating: 'bookRating',
  bookSeriesId: 'bookSeriesId',
  bookSeriesNumber: 'bookSeriesNumber',
  bookSeriesTitle: 'bookSeriesTitle',
  bookTranslator: 'bookTranslator',
  bookType: 'bookType',
  bookValue: 'bookValue',
  bot: 'bot',
  botResponses: 'botResponses',
  botResponsesValue: 'botResponsesValue',
  botValue: 'botValue',
  brandColor: 'brandColor',
  brandColorName: 'brandColorName',
  brandColorNameValue: 'brandColorNameValue',
  brandColorValue: 'brandColorValue',
  bubbleTag: 'bubbleTag',
  bubbleTagValue: 'bubbleTagValue',
  buttonCaption: 'buttonCaption',
  buttonCaptionValue: 'buttonCaptionValue',
  callToActionUrl: 'callToActionUrl',
  callToActionUrlValue: 'callToActionUrlValue',
  caption: 'caption',
  captionDefinitionList: 'captionDefinitionList',
  captionValue: 'captionValue',
  cardBits: 'cardBits',
  cardBitsValue: 'cardBitsValue',
  cardNode: 'cardNode',
  categoryTag: 'categoryTag',
  categoryTagValue: 'categoryTagValue',
  cells: 'cells',
  cellsValue: 'cellsValue',
  chatWithBook: 'chatWithBook',
  chatWithBookBrainKey: 'chatWithBookBrainKey',
  chatWithBookBrainKeyValue: 'chatWithBookBrainKeyValue',
  chatWithBookValue: 'chatWithBookValue',
  checked: 'checked',
  choice: 'choice',
  choices: 'choices',
  choicesValue: 'choicesValue',
  citationStyle: 'citationStyle',
  citationStyleValue: 'citationStyleValue',
  class: 'class',
  classification: 'classification',
  classificationValue: 'classificationValue',
  codeLineNumbers: 'codeLineNumbers',
  codeLineNumbersValue: 'codeLineNumbersValue',
  codeMinimap: 'codeMinimap',
  codeMinimapValue: 'codeMinimapValue',
  color: 'color',
  colorTag: 'colorTag',
  colorTagValue: 'colorTagValue',
  column: 'column',
  columns: 'columns',
  columnsValue: 'columnsValue',
  comment: 'comment',
  commentedBitType: 'commentedBitType',
  computerLanguage: 'computerLanguage',
  computerLanguageValue: 'computerLanguageValue',
  content: 'contentValue',
  content2Buy: 'content2Buy',
  contentValue: 'contentValue',
  contentValueValue: 'contentValueValue',
  copyright: 'copyright',
  coverColor: 'coverColor',
  coverColorValue: 'coverColorValue',
  coverImage: 'coverImage',
  coverImageValue: 'coverImageValue',
  currency: 'currency',
  customerExternalId: 'customerExternalId',
  customerId: 'customerId',
  customerIdValue: 'customerIdValue',
  customerProductId: 'customerProductId',
  customerProductShopLink: 'customerProductShopLink',
  data: 'data',
  dataValue: 'dataValue',
  dataValueValue: 'dataValueValue',
  date: 'date',
  dateEnd: 'dateEnd',
  dateEndValue: 'dateEndValue',
  dateValue: 'dateValue',
  decimalPlaces: 'decimalPlaces',
  deeplink: 'deeplink',
  deeplinkValue: 'deeplinkValue',
  definition: 'definition',
  definitions: 'definitions',
  definitionsValue: 'definitionsValue',
  displayName: 'displayName',
  displayNameValue: 'displayNameValue',
  diffContext: 'diffContext',
  diffContextValue: 'diffContextValue',
  diffOp: 'diffOp',
  diffOpValue: 'diffOpValue',
  diffRef: 'diffRef',
  diffRefValue: 'diffRefValue',
  diffTime: 'diffTime',
  diffTimeValue: 'diffTimeValue',
  diffTo: 'diffTo',
  diffToValue: 'diffToValue',
  disableCalculation: 'disableCalculation',
  disableFeedback: 'disableFeedback',
  disableFeedbackValue: 'disableFeedbackValue',
  document: 'document',
  documentDownload: 'documentDownload',
  documentEmbed: 'documentEmbed',
  documentLink: 'documentLink',
  duration: 'duration',
  durationValue: 'durationValue',
  elements: 'elements',
  elementsValue: 'elementsValue',
  elementsValueValue: 'elementsValueValue',
  emphasis: 'emphasis',
  end: 'end',
  enforceUpdateOverUserInput: 'enforceUpdateOverUserInput',
  enforceUpdateOverUserInputValue: 'enforceUpdateOverUserInputValue',
  errors: 'errors',
  errorsValue: 'errorsValue',
  example: 'example',
  exampleValue: 'exampleValue',
  externalId: 'externalId',
  externalIdValue: 'externalIdValue',
  externalLink: 'externalLink',
  externalLinkText: 'externalLinkText',
  extractorExtractionTimestamp: 'extractorExtractionTimestamp',
  extractorExtractionTimestampValue: 'extractorExtractionTimestampValue',
  extractorTag: 'extractorTag',
  extractorTagValue: 'extractorTagValue',
  extractorInternal: 'extractorInternal',
  extractorInternalValue: 'extractorInternalValue',
  extraProperties: 'extraProperties',
  feedback: 'feedback',
  feedbackEngine: 'feedbackEngine',
  feedbackEngineValue: 'feedbackEngineValue',
  feedbacks: 'feedbacks',
  feedbacksValue: 'feedbacksValue',
  feedbackType: 'feedbackType',
  flashcards: 'flashcards',
  flashcardSet: 'flashcardSet',
  flashcardSetValue: 'flashcardSetValue',
  flashcardsValue: 'flashcardsValue',
  focusX: 'focusX',
  focusXValue: 'focusXValue',
  focusY: 'focusY',
  focusYValue: 'focusYValue',
  footer: 'footer',
  footerText: 'footerText',
  footerValue: 'footerValue',
  forKeys: 'forKeys',
  format: 'format',
  formula: 'formula',
  formulaValue: 'formulaValue',
  forValues: 'forValues',
  forValuesValue: 'forValuesValue',
  fullName: 'fullName',
  fullNameValue: 'fullNameValue',
  gap: 'gap',
  gmbExternalShop: 'gmbExternalShop',
  gmbExternalShopItems: 'gmbExternalShopItems',
  groupTag: 'groupTag',
  groupTagValue: 'groupTagValue',
  gUri: 'gUri',
  gUriValue: 'gUriValue',
  handInAcceptFileType: 'handInAcceptFileType',
  handInAcceptFileTypeValue: 'handInAcceptFileTypeValue',
  handInInstruction: 'handInInstruction',
  handInInstructionValue: 'handInInstructionValue',
  handInRequirement: 'handInRequirement',
  handInRequirementValue: 'handInRequirementValue',
  hasBookNavigation: 'hasBookNavigation',
  hasBookNavigationValue: 'hasBookNavigationValue',
  hasMarkAsDone: 'hasMarkAsDone',
  hasMarkAsDoneValue: 'hasMarkAsDoneValue',
  hasPrintRestriction: 'hasPrintRestriction',
  hasPrintRestrictionValue: 'hasPrintRestrictionValue',
  head: 'head',
  heading: 'heading',
  height: 'height',
  heightValue: 'heightValue',
  highlight: 'highlight',
  hint: 'hint',
  hintValue: 'hintValue',
  href: 'href',
  icon: 'icon',
  iconTag: 'iconTag',
  iconTagValue: 'iconTagValue',
  iconValue: 'iconValue',
  id: 'id',
  idValue: 'idValue',
  iframeName: 'iframeName',
  iframeSrc: 'iframeSrc',
  image: 'image',
  imageFirst: 'imageFirst',
  imageFirstValue: 'imageFirstValue',
  imageLandscape: 'imageLandscape',
  imageLink: 'imageLink',
  imagePlaceholder: 'imagePlaceholder',
  imagePlaceholderValue: 'imagePlaceholderValue',
  imagePortrait: 'imagePortrait',
  imageSource: 'imageSource',
  index: 'index',
  indexValue: 'indexValue',
  ingredients: 'ingredients',
  ingredientsValue: 'ingredientsValue',
  ingredient: 'ingredient',
  ingredientValue: 'ingredientValue',
  instruction: 'instruction',
  instructionValue: 'instructionValue',
  internalComment: 'internalComment',
  internalCommentValue: 'internalCommentValue',
  internalPrintPdf: 'internalPrintPdf',
  internalPrintPdfValue: 'internalPrintPdfValue',
  isCaseSensitive: 'isCaseSensitive',
  isCollapsible: 'isCollapsible',
  isCollapsibleValue: 'isCollapsibleValue',
  isCommented: 'isCommented',
  isCorrect: 'isCorrect',
  isEditable: 'isEditable',
  isEditableValue: 'isEditableValue',
  isExample: 'isExample',
  isInfoOnly: 'isInfoOnly',
  isInfoOnlyValue: 'isInfoOnlyValue',
  isPlain: 'isPlain',
  isPublic: 'isPublic',
  isPublicValue: 'isPublicValue',
  isTemplate: 'isTemplate',
  isTemplateStripTheme: 'isTemplateStripTheme',
  isTemplateStripThemeValue: 'isTemplateStripThemeValue',
  isTemplateValue: 'isTemplateValue',
  isTracked: 'isTracked',
  isTrackedValue: 'isTrackedValue',
  item: 'item',
  itemLead: 'itemLead',
  itemValue: 'itemValue',
  jobTitle: 'jobTitle',
  jobTitleValue: 'jobTitleValue',
  jupyterExecutionCount: 'jupyterExecutionCount',
  jupyterExecutionCountValue: 'jupyterExecutionCountValue',
  jupyterId: 'jupyterId',
  jupyterIdValue: 'jupyterIdValue',
  key: 'key',
  keyAudio: 'keyAudio',
  keyImage: 'keyImage',
  kind: 'kind',
  kindValue: 'kindValue',
  label: 'label',
  labelFalse: 'labelFalse',
  labelFalseValue: 'labelFalseValue',
  labelTrue: 'labelTrue',
  labelTrueValue: 'labelTrueValue',
  lang: 'lang',
  language: 'language',
  languageValue: 'languageValue',
  langValue: 'langValue',
  layer: 'layer',
  layerRole: 'layerRole',
  layerRoleValue: 'layerRoleValue',
  layerValue: 'layerValue',
  lead: 'lead',
  leadValue: 'leadValue',
  level: 'level',
  levelACTFL: 'levelACTFL',
  levelACTFLValue: 'levelACTFLValue',
  levelCEFR: 'levelCEFR',
  levelCEFRp: 'levelCEFRp',
  levelCEFRpValue: 'levelCEFRpValue',
  levelCEFRValue: 'levelCEFRValue',
  levelILR: 'levelILR',
  levelILRValue: 'levelILRValue',
  license: 'license',
  line: 'line',
  list: 'list',
  listItemIndent: 'listItemIndent',
  listItemIndentValue: 'listItemIndentValue',
  listPrice: 'listPrice',
  listValue: 'listValue',
  location: 'location',
  locationValue: 'locationValue',
  machineTranslated: 'machineTranslated',
  machineTranslatedValue: 'machineTranslatedValue',
  mailingList: 'mailingList',
  marginNumber: 'marginNumber',
  marginNumberValue: 'marginNumberValue',
  mark: 'mark',
  markConfig: 'markConfig',
  markConfigValue: 'markConfigValue',
  marks: 'marks',
  marksValue: 'marksValue',
  markup: 'markup', // bitmark markup
  matrix: 'matrix',
  matrixValue: 'matrixValue',
  maxCreatedBits: 'maxCreatedBits',
  maxCreatedBitsValue: 'maxCreatedBitsValue',
  maxDisplayLevel: 'maxDisplayLevel',
  maxDisplayLevelValue: 'maxDisplayLevelValue',
  maxTocChapterLevel: 'maxTocChapterLevel',
  maxTocChapterLevelValue: 'maxTocChapterLevelValue',
  message: 'message',
  mockupId: 'mockupId',
  name: 'name',
  needsValidation: 'needsValidation',
  needsValidationValue: 'needsValidationValue',
  offset: 'offset',
  options: 'options',
  optionsValue: 'optionsValue',
  original: 'original',
  padletId: 'padletId',
  padletIdValue: 'padletIdValue',
  page: 'page',
  pageNo: 'pageNo',
  pageNoValue: 'pageNoValue',
  pageNumber: 'pageNumber',
  pageNumberValue: 'pageNumberValue',
  pageValue: 'pageValue',
  pairs: 'pairs',
  pairsValue: 'pairsValue',
  parent: 'parent',
  parser: 'parser',
  partialAnswer: 'partialAnswer',
  partialAnswerValue: 'partialAnswerValue',
  path: 'path',
  pathValue: 'pathValue',
  person: 'person',
  platformBackgroundColor: 'platformBackgroundColor',
  platformBackgroundColorValue: 'platformBackgroundColorValue',
  platformBackgroundImage: 'platformBackgroundImage',
  platformBackgroundImageValue: 'platformBackgroundImageValue',
  platformBaseLayerColor: 'platformBaseLayerColor',
  platformBaseLayerColorValue: 'platformBaseLayerColorValue',
  platformBorderColor: 'platformBorderColor',
  platformBorderColorValue: 'platformBorderColorValue',
  platformBorderRadius: 'platformBorderRadius',
  platformBorderRadiusValue: 'platformBorderRadiusValue',
  platformBrandTarget: 'platformBrandTarget',
  platformBrandTargetValue: 'platformBrandTargetValue',
  platformButtonBackgroundColor: 'platformButtonBackgroundColor',
  platformButtonBackgroundColorValue: 'platformButtonBackgroundColorValue',
  platformButtonPrimaryColor: 'platformButtonPrimaryColor',
  platformButtonPrimaryColorValue: 'platformButtonPrimaryColorValue',
  platformIcon: 'platformIcon',
  platformIconValue: 'platformIconValue',
  platformLogo: 'platformLogo',
  platformLogoMaxHeight: 'platformLogoMaxHeight',
  platformLogoMaxHeightValue: 'platformLogoMaxHeightValue',
  platformLogoValue: 'platformLogoValue',
  platformMargin: 'platformMargin',
  platformMarginValue: 'platformMarginValue',
  platformMessageBackgroundColor: 'platformMessageBackgroundColor',
  platformMessageBackgroundColorValue: 'platformMessageBackgroundColorValue',
  platformName: 'platformName',
  platformNameValue: 'platformNameValue',
  platformNeedsShadow: 'platformNeedsShadow',
  platformNeedsShadowValue: 'platformNeedsShadowValue',
  platformPlaceholderColor: 'platformPlaceholderColor',
  platformPlaceholderColorValue: 'platformPlaceholderColorValue',
  platformPrimaryColor: 'platformPrimaryColor',
  platformPrimaryColorValue: 'platformPrimaryColorValue',
  platformScrollbarColor: 'platformScrollbarColor',
  platformScrollbarColorValue: 'platformScrollbarColorValue',
  platformSecondaryColor: 'platformSecondaryColor',
  platformSecondaryColorValue: 'platformSecondaryColorValue',
  platformSelectionBorderRadius: 'platformSelectionBorderRadius',
  platformSelectionBorderRadiusValue: 'platformSelectionBorderRadiusValue',
  platformSelectionColor: 'platformSelectionColor',
  platformSelectionColorValue: 'platformSelectionColorValue',
  platformSelectionTextColor: 'platformSelectionTextColor',
  platformSelectionTextColorValue: 'platformSelectionTextColorValue',
  platformSeparatorColor: 'platformSeparatorColor',
  platformSeparatorColorValue: 'platformSeparatorColorValue',
  platformTextSelectionColor: 'platformTextSelectionColor',
  platformTextSelectionColorValue: 'platformTextSelectionColorValue',
  pointerLeft: 'pointerLeft',
  pointerLeftValue: 'pointerLeftValue',
  pointerTop: 'pointerTop',
  pointerTopValue: 'pointerTopValue',
  posterImage: 'posterImage',
  posterImageValue: 'posterImageValue',
  postfix: 'postfix',
  prefix: 'prefix',
  previewImage: 'previewImage',
  previewImageValue: 'previewImageValue',
  previewVideo: 'previewVideo',
  previewVideoValue: 'previewVideoValue',
  printParentChapterLevel: 'printParentChapterLevel',
  printParentChapterLevelValue: 'printParentChapterLevelValue',
  printOnRequestCustomerId: 'printOnRequestCustomerId',
  printOnRequestCustomerIdValue: 'printOnRequestCustomerIdValue',
  printOnRequestProductId: 'printOnRequestProductId',
  printOnRequestProductIdValue: 'printOnRequestProductIdValue',
  processHandIn: 'processHandIn',
  processHandInLocation: 'processHandInLocation',
  processHandInLocationValue: 'processHandInLocationValue',
  processHandInValue: 'processHandInValue',
  product: 'product',
  productFolder: 'productFolder',
  productFolderValue: 'productFolderValue',
  productId: 'productId',
  productIdValue: 'productIdValue',
  productList: 'productList',
  productListValue: 'productListValue',
  productValue: 'productValue',
  productVideo: 'productVideo',
  productVideoList: 'productVideoList',
  productVideoListValue: 'productVideoListValue',
  productVideoValue: 'productVideoValue',
  progress: 'progress',
  progressValue: 'progressValue',
  pronunciationTable: 'pronunciationTable',
  properties: 'properties',
  property: 'property',
  propertyKey: 'propertyKey',
  propertyValue: 'propertyValue',
  propertyValues: 'propertyValues',
  provider: 'provider',
  pseudonym: 'pseudonym',
  pseudonymValue: 'pseudonymValue',
  publications: 'publications',
  publicationsValue: 'publicationsValue',
  publisher: 'publisher',
  publisherName: 'publisherName',
  publisherNameValue: 'publisherNameValue',
  publisherValue: 'publisherValue',
  quantity: 'quantity',
  question: 'question',
  questions: 'questions',
  questionsValue: 'questionsValue',
  questionValue: 'questionValue',
  quizCountItems: 'quizCountItems',
  quizCountItemsValue: 'quizCountItemsValue',
  quizStrikethroughSolutions: 'quizStrikethroughSolutions',
  quizStrikethroughSolutionsValue: 'quizStrikethroughSolutionsValue',
  quizzes: 'quizzes',
  quizzesValue: 'quizzesValue',
  quotedPerson: 'quotedPerson',
  ratingLevelEnd: 'ratingLevelEnd',
  ratingLevelSelected: 'ratingLevelSelected',
  ratingLevelSelectedValue: 'ratingLevelSelectedValue',
  ratingLevelStart: 'ratingLevelStart',
  reaction: 'reaction',
  reason: 'reason',
  reasonableNumOfChars: 'reasonableNumOfChars',
  reasonableNumOfCharsValue: 'reasonableNumOfCharsValue',
  reductionTag: 'reductionTag',
  reductionTagValue: 'reductionTagValue',
  refAuthor: 'refAuthor',
  refBookTitle: 'refBookTitle',
  reference: 'reference',
  referenceEnd: 'referenceEnd',
  referenceProperty: 'referenceProperty',
  referencePropertyValue: 'referencePropertyValue',
  refPublicationYear: 'refPublicationYear',
  refPublisher: 'refPublisher',
  relatedBook: 'relatedBook',
  relatedBookValue: 'relatedBookValue',
  translationOfBook: 'translationOfBook',
  translationOfBookValue: 'translationOfBookValue',
  userFeedbackTranslationUrl: 'userFeedbackTranslationUrl',
  userFeedbackTranslationUrlValue: 'userFeedbackTranslationUrlValue',
  userFeedbackContentUrl: 'userFeedbackContentUrl',
  userFeedbackContentUrlValue: 'userFeedbackContentUrlValue',
  releaseDate: 'releaseDate',
  releaseDateValue: 'releaseDateValue',
  releaseKind: 'releaseKind',
  releaseKindValue: 'releaseKindValue',
  releaseVersion: 'releaseVersion',
  releaseVersionValue: 'releaseVersionValue',
  reportTag: 'reportTag',
  reportTagValue: 'reportTagValue',
  resolved: 'resolved',
  resolvedBy: 'resolvedBy',
  resolvedByValue: 'resolvedByValue',
  resolvedDate: 'resolvedDate',
  resolvedDateValue: 'resolvedDateValue',
  resolvedValue: 'resolvedValue',
  resources: 'resources',
  resourcesValue: 'resourcesValue',
  resourceType: 'resourceType',
  response: 'response',
  responses: 'responses',
  responsesValue: 'responsesValue',
  revealSolutions: 'revealSolutions',
  reviewTag: 'reviewTag',
  reviewTagValue: 'reviewTagValue',
  reviewerTag: 'reviewerTag',
  reviewerTagValue: 'reviewerTagValue',
  rightsPolicy: 'rightsPolicy',
  rightsPolicyValue: 'rightsPolicyValue',
  root: 'root', // bit type (root)
  rows: 'rows',
  rowsValue: 'rowsValue',
  rtl: 'rtl',
  rtlValue: 'rtlValue',
  rubric: 'rubric',
  rubricValue: 'rubricValue',
  sampleSolution: 'sampleSolution',
  sampleSolutionValue: 'sampleSolutionValue',
  scormSource: 'scormSource',
  scormSourceValue: 'scormSourceValue',
  search: 'search',
  searchIndex: 'searchIndex',
  searchIndexValue: 'searchIndexValue',
  searchValue: 'searchValue',
  section: 'section',
  select: 'select',
  selected: 'selected',
  servings: 'servings',
  servingsValue: 'servingsValue',
  showInIndex: 'showInIndex',
  showInIndexValue: 'showInIndexValue',
  size: 'size',
  slug: 'slug',
  slugValue: 'slugValue',
  solution: 'solution',
  solutions: 'solutions',
  solutionsValue: 'solutionsValue',
  sourceBB: 'sourceBB',
  sourceBBValue: 'sourceBBValue',
  sourceDocument: 'sourceDocument',
  sourceDocumentValue: 'sourceDocumentValue',
  sourceRL: 'sourceRL',
  sourceRLValue: 'sourceRLValue',
  spaceId: 'spaceId',
  spaceIdValue: 'spaceIdValue',
  spansPageBreak: 'spansPageBreak',
  spansPageBreakValue: 'spansPageBreakValue',
  src: 'src',
  src1x: 'src1x',
  src2x: 'src2x',
  src3x: 'src3x',
  src4x: 'src4x',
  srcAlt: 'srcAlt',
  srcAltValue: 'srcAltValue',
  start: 'start',
  statement: 'statement',
  statementValue: 'statementValue',
  statements: 'statements',
  statementsValue: 'statementsValue',
  statementText: 'statementText',
  stillImageFilmEmbed: 'stillImageFilmEmbed',
  stillImageFilmLink: 'stillImageFilmLink',
  stripePricingTableId: 'stripePricingTableId',
  stripePricingTableIdValue: 'stripePricingTableIdValue',
  stripePricingTableDescriptiveName: 'stripePricingTableDescriptiveName',
  stripePricingTableDescriptiveNameValue: 'stripePricingTableDescriptiveNameValue',
  stripePublishableKey: 'stripePublishableKey',
  stripePublishableKeyValue: 'stripePublishableKeyValue',
  subject: 'subject',
  subjectValue: 'subjectValue',
  subtitle: 'subtitle',
  subtitleValue: 'subtitleValue',
  subtype: 'subtype',
  subtypeValue: 'subtypeValue',
  table: 'table',
  tableAutoWidth: 'tableAutoWidth',
  tableAutoWidthValue: 'tableAutoWidthValue',
  tableColWidth: 'tableColWidth',
  tableColWidthValue: 'tableColWidthValue',
  tableColumnMinWidth: 'tableColumnMinWidth',
  tableColumnMinWidthValue: 'tableColumnMinWidthValue',
  tableFixedHeader: 'tableFixedHeader',
  tableFixedHeaderValue: 'tableFixedHeaderValue',
  tableHeaderWhitespaceNoWrap: 'tableHeaderWhitespaceNoWrap',
  tableHeaderWhitespaceNoWrapValue: 'tableHeaderWhitespaceNoWrapValue',
  tableHeight: 'tableHeight',
  tableHeightValue: 'tableHeightValue',
  tablePagination: 'tablePagination',
  tablePaginationLimit: 'tablePaginationLimit',
  tablePaginationLimitValue: 'tablePaginationLimitValue',
  tablePaginationValue: 'tablePaginationValue',
  tableResizableColumns: 'tableResizableColumns',
  tableResizableColumnsValue: 'tableResizableColumnsValue',
  tableSearch: 'tableSearch',
  tableSearchValue: 'tableSearchValue',
  tableSort: 'tableSort',
  tableSortValue: 'tableSortValue',
  tableWhitespaceNoWrap: 'tableWhitespaceNoWrap',
  tableWhitespaceNoWrapValue: 'tableWhitespaceNoWrapValue',
  tag: 'tag',
  tagValue: 'tagValue',
  target: 'target',
  targetValue: 'targetValue',
  technicalTerm: 'technicalTerm',
  technicalTermValue: 'technicalTermValue',
  term: 'term',
  text: 'text',
  textAlign: 'textAlign',
  textAst: 'textAst',
  textAstValue: 'textAstValue',
  textFormat: 'textFormat',
  textReference: 'textReference',
  textReferenceValue: 'textReferenceValue',
  texts: 'texts',
  textsValue: 'textsValue',
  theme: 'theme',
  themeValue: 'themeValue',
  thumbImage: 'thumbImage',
  thumbImageValue: 'thumbImageValue',
  thumbnails: 'thumbnails',
  thumbnailsValue: 'thumbnailsValue',
  title: 'title',
  titleString: 'titleString',
  titleValue: 'titleValue',
  toc: 'toc',
  tocContent: 'tocContent',
  tocContentValue: 'tocContentValue',
  tocResource: 'tocResource',
  tocResourceValue: 'tocResourceValue',
  tocValue: 'tocValue',
  topicTag: 'topicTag',
  topicTagValue: 'topicTagValue',
  translationOf: 'translationOf',
  translationOfValue: 'translationOfValue',
  trim: 'trim',
  type: 'type',
  unit: 'unit',
  unitAbbr: 'unitAbbr',
  unitAbbrPlural: 'unitAbbrPlural',
  unitPlural: 'unitPlural',
  url: 'url',
  validationDate: 'validationDate',
  validationDateValue: 'validationDateValue',
  value: 'value',
  values: 'values',
  valuesValue: 'valuesValue',
  vendorDashboardId: 'vendorDashboardId',
  vendorDashboardIdValue: 'vendorDashboardIdValue',
  vendorSurveyId: 'vendorSurveyId',
  vendorSurveyIdValue: 'vendorSurveyIdValue',
  vendorUrl: 'vendorUrl',
  vendorUrlValue: 'vendorUrlValue',
  version: 'version',
  video: 'video',
  videoCallLink: 'videoCallLink',
  videoCallLinkValue: 'videoCallLinkValue',
  videoEmbed: 'videoEmbed',
  videoLink: 'videoLink',
  warnings: 'warnings',
  warningsValue: 'warningsValue',
  websiteLink: 'websiteLink',
  width: 'width',
  widthValue: 'widthValue',
  x: 'x',
  xValue: 'xValue',
  y: 'y',
  yValue: 'yValue',
  zoomDisabled: 'zoomDisabled',
} as const;

export type NodeTypeType = EnumType<typeof NodeType>;

export { NodeType };
